import { useState, useEffect } from 'react';

const STATUS_COLORS = {
  open: '#3b82f6',
  assigned: '#f59e0b',
  in_progress: '#8b5cf6',
  completed: '#10b981'
};

const STATUS_LABELS = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  completed: 'Completed'
};

const SOURCE_LABELS = {
  inspection: 'Inspection',
  manual: 'Manual',
  driver_report: 'Driver Report',
  maintenance_due: 'Scheduled',
  incident: 'Truck Issue',
  accident: 'Accident',
};

function formatTimeInQueue(createdAt) {
  if (!createdAt) return '';
  const created = new Date(createdAt.includes('T') ? createdAt : createdAt.replace(' ', 'T') + 'Z');
  const now = Date.now();
  const diffMs = now - created.getTime();
  if (diffMs < 0) return '0m';
  const totalMinutes = Math.floor(diffMs / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z');
  return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric' });
}

export default function WorkOrderCard({ workOrder, user, can, onAssign, onClaim, onComplete, onClose, onComment }) {
  const [timeDisplay, setTimeDisplay] = useState(() => formatTimeInQueue(workOrder.created_at));

  useEffect(() => {
    setTimeDisplay(formatTimeInQueue(workOrder.created_at));
    const interval = setInterval(() => {
      setTimeDisplay(formatTimeInQueue(workOrder.created_at));
    }, 60000);
    return () => clearInterval(interval);
  }, [workOrder.created_at]);

  const isAdmin = ['admin', 'super_admin'].includes(user.permission_tier);
  const isAssignedToMe = workOrder.assigned_to === user.id;
  const isUrgent = workOrder.priority === 'urgent';
  const status = workOrder.status;

  return (
    <div className={`call-card status-${status}${isUrgent ? ' emergency' : ''}`}>
      {/* Header: status badge + priority badge + timer */}
      <div className="call-card-header">
        <div className="call-card-left">
          <span className="status-badge" style={{ background: STATUS_COLORS[status] || STATUS_COLORS.open }}>
            {STATUS_LABELS[status] || status}
          </span>
          <span className={`priority-badge priority-${workOrder.priority || 'normal'}`}>
            {(workOrder.priority || 'normal').toUpperCase()}
          </span>
          {isUrgent && <span className="emergency-badge">URGENT</span>}
        </div>
        <span className="time-in-queue">{timeDisplay}</span>
      </div>

      {/* Body: title + details */}
      <div className="call-card-body">
        <div className="call-property">{workOrder.title}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13.5px', color: 'var(--text-muted)' }}>
          <span>Truck #{workOrder.unit_number || 'Unassigned'}</span>
          {workOrder.assigned_to_name && <span>Assigned: <strong>{workOrder.assigned_to_name}</strong></span>}
          {workOrder.shop_name && <span>Shop: {workOrder.shop_name}</span>}
          {workOrder.due_date && <span>Due: {formatDate(workOrder.due_date)}</span>}
        </div>
        {/* Task Board strip — surfaces the linked workflow_item so the card
            shows "Current Step + Owner Now" without opening the Task Board.
            Renders only when a work_order workflow template is configured
            (wf_stage_name comes from the server JOIN). */}
        {workOrder.wf_stage_name && (
          <div style={{ marginTop: 8, padding: '6px 8px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 12 }}>
            <span style={{
              display: 'inline-block', padding: '1px 8px', borderRadius: 4,
              background: workOrder.wf_stage_color || '#6b7280', color: '#fff',
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
              marginRight: 6,
            }}>{workOrder.wf_stage_name}</span>
            <span style={{ color: '#475569' }}>
              {workOrder.wf_owner_names || 'Unassigned'}
            </span>
          </div>
        )}
        {workOrder.source && (
          <div style={{ marginTop: '8px' }}>
            <span className="call-type-badge">{SOURCE_LABELS[workOrder.source] || workOrder.source}</span>
          </div>
        )}
      </div>

      {/* Footer: action buttons */}
      <div className="call-card-actions">
        <button className="btn btn-outline btn-sm" onClick={() => onComment && onComment(workOrder)}>
          💬 {workOrder.comment_count > 0 ? workOrder.comment_count : ''}
        </button>
        {status === 'open' && isAdmin && (
          <button className="btn btn-gold btn-sm" onClick={() => onAssign(workOrder)}>Assign</button>
        )}
        {status === 'assigned' && (isAssignedToMe || isAdmin) && (
          <button className="btn btn-gold btn-sm" onClick={() => onClaim(workOrder.id)}>Start Work</button>
        )}
        {status === 'in_progress' && (isAssignedToMe || isAdmin) && (
          <button className="btn btn-success btn-sm" onClick={() => onComplete(workOrder)}>Complete</button>
        )}
        {status === 'completed' && isAdmin && (
          <button className="btn btn-success btn-sm" onClick={() => onClose(workOrder)}>Close-Out</button>
        )}
      </div>
    </div>
  );
}
